/**
 * Widget SLA Countdown (E10.7 — RF-6-1/RF-6-2/RF-13-2).
 *
 * Mostra per ogni timer SLA le soglie con il tempo rimanente formattato
 * in giorni/ore/minuti. Il colore varia in base allo status:
 *   ok      → verde
 *   warning → arancione
 *   breached → rosso (card evidenziata)
 *   stopped → timer fermo (step registrato — in tempo o in ritardo — o thread chiuso)
 *
 * Alimentato da GET /sla/timers dell'srp-service.
 */

import { useSlaTimers } from './hooks.js';
import type { SlaStatus, SlaThreshold } from './types.js';
import styles from './Srp.module.css';

// ---------------------------------------------------------------------------
// Formattazione countdown
// ---------------------------------------------------------------------------

/**
 * Converte millisecondo in stringa leggibile gg hh mm.
 * Se il valore è negativo (breached) mostra "scaduto".
 */
export function formatRemainingMs(ms: number): string {
  if (ms <= 0) return 'scaduto';
  const totMinuti = Math.floor(ms / 60_000);
  const gg = Math.floor(totMinuti / (60 * 24));
  const hh = Math.floor((totMinuti % (60 * 24)) / 60);
  const mm = totMinuti % 60;
  const parti: string[] = [];
  if (gg > 0) parti.push(`${gg}g`);
  if (hh > 0 || gg > 0) parti.push(`${hh}h`);
  parti.push(`${mm}m`);
  return parti.join(' ');
}

/** Restituisce la classe CSS in base allo status SLA. */
function classeSlaStatus(status: SlaStatus): string {
  if (status === 'ok' || status === 'stopped') return styles.slaOk ?? '';
  if (status === 'warning') return styles.slaWarning ?? '';
  return styles.slaBreached ?? '';
}

// ---------------------------------------------------------------------------
// Componente riga soglia
// ---------------------------------------------------------------------------

interface SogliaRowProps {
  label: string;
  soglia: SlaThreshold;
}

/** Testo dello stato: timer fermo per step registrato (in tempo / in ritardo) o thread chiuso. */
function testoStato(soglia: SlaThreshold): string {
  if (soglia.status !== 'stopped') return soglia.status;
  if (soglia.stoppedBy === 'thread_closed') return 'fermo (thread chiuso)';
  return soglia.late ? 'fermo (in ritardo)' : 'fermo (in tempo)';
}

function SogliaRow({ label, soglia }: SogliaRowProps) {
  const fermo = soglia.status === 'stopped';
  return (
    <div className={styles.slaThresholdRow}>
      <span className={styles.slaThresholdLabel}>{label}</span>
      <span className={`${styles.slaCountdown} ${classeSlaStatus(soglia.status)}`}>
        {fermo ? '—' : formatRemainingMs(soglia.remainingMs)}
      </span>
      <span
        className={classeSlaStatus(soglia.status)}
        data-testid={`sla-status-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        {testoStato(soglia)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principale SlaCountdown
// ---------------------------------------------------------------------------

/** Props opzionali per testabilità senza fetch (come TriagePage). */
export interface SlaCountdownProps {
  /** Timer iniettati direttamente (usati nei test; in produzione si usa useSlaTimers). */
  timers?: ReturnType<typeof useSlaTimers>['data'];
  /** Se true, non chiama useSlaTimers (usato nei test per evitare fetch). */
  skipFetch?: boolean;
}

export function SlaCountdown({ timers: timersProp, skipFetch = false }: SlaCountdownProps = {}) {
  const query = useSlaTimers();
  // In test, skipFetch=true e timersProp viene passato direttamente
  const timers = skipFetch ? timersProp : (timersProp ?? query.data);
  const isLoading = skipFetch ? false : query.isLoading;
  const isError = skipFetch ? false : query.isError;

  return (
    <div className={styles.slaWidget} data-testid="sla-countdown-widget">
      <p className={styles.slaWidgetTitle}>Countdown SLA (E10.7)</p>

      {isLoading && (
        <p className={styles.loadingMsg} data-testid="sla-loading">
          Caricamento timer SLA...
        </p>
      )}

      {isError && (
        <div className={styles.errorMsg} data-testid="sla-error">
          Impossibile caricare i timer SLA.
        </div>
      )}

      {!isLoading && !isError && timers && timers.length === 0 && (
        <div className={styles.emptyState} data-testid="sla-vuoto">
          Nessun timer SLA attivo.
        </div>
      )}

      {!isLoading && !isError && timers && timers.length > 0 && (
        <div className={styles.slaGrid} data-testid="sla-grid">
          {timers.map((timer) => {
            // La card è evidenziata se almeno una soglia è in breach
            const hasBreach =
              timer.slaState.early.status === 'breached' ||
              timer.slaState.notification.status === 'breached' ||
              timer.slaState.final?.status === 'breached';

            return (
              <div
                key={timer.id}
                className={`${styles.slaCard} ${hasBreach ? (styles.slaBreachedCard ?? '') : ''}`}
                data-testid={`sla-card-${timer.id}`}
              >
                <p className={styles.slaCardTitle}>KEV Finding</p>
                <p className={styles.slaCardSubject}>{timer.subjectRef}</p>
                <div className={styles.slaThresholds}>
                  <SogliaRow label="Preavviso (24h)" soglia={timer.slaState.early} />
                  <SogliaRow label="Notifica (72h)" soglia={timer.slaState.notification} />
                  {timer.slaState.final && (
                    <SogliaRow label="Relazione finale" soglia={timer.slaState.final} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
